import { computed } from "vue";
import { CARD_WIDTHS } from "~/utils/imageSizes";
import { mapFirebaseUrlToProxy } from "~/utils/imageUrl";
import { useImageOptimizer } from "~/composables/useImageOptimizer";

/* ====================================================================
 *  useCardImage — обкладинка картки товару:
 *  – вибір кандидата з denorm-полів варіанта/товару;
 *  – мапінг Firebase Storage URL → наш проксі /api/img/ — `utils/imageUrl`;
 *  – оптимізація — спільний `useImageOptimizer` (там же описані пастки
 *    провайдерів; галерея товару користується ним же, п.9г).
 *
 *  ЧОМУ НЕ РУЧНИЙ URL (п.9в). Тут раніше стояв рядок
 *  `/_ipx/f_webp&q_75&fit_cover&s_600x800/…`, зібраний вручну. На Vercel
 *  маршруту /_ipx/ не існує — кожна картка отримувала 404 і лише потім
 *  показувала оригінал через фолбек. Тобто оптимізації на проді не було
 *  жодного дня: ні webp, ні ресайзу, а srcset був мертвий цілком.
 *  Тепер URL будує `useImage()` — він сам підставить /_vercel/image на
 *  проді і /_ipx/ локально.
 *
 *  ЩО ВТРАТИЛИ І ЧОМУ ЦЕ ОК: провайдер `vercel` вміє лише ширину і
 *  якість — `height`, `fit=cover` і `format` він ігнорує. Кроп до 3:4
 *  робить CSS (`object-cover`), а формат домовляється по `Accept`
 *  (у білд-конфізі оголошені webp і avif), тож віддається avif.
 *
 *  РЕЗОЛВ СИНХРОННИЙ І SSR-НИЙ. До п.9а дані каталогу приходили в
 *  клієнті, тому обкладинка рахувалась в onMounted; тепер /api/catalog
 *  віддає ті самі поля на сервері, і `computed` дає <img> уже в сирому
 *  HTML — картинка починає вантажитись, не чекаючи гідрації.
 * ==================================================================== */

type AnyRecord = Record<string, unknown>;

/** Кандидати на обкладинку, у порядку пріоритету: варіант → товар. */
function pickCover(
  product: AnyRecord | null | undefined,
  variant: AnyRecord | null | undefined,
): string {
  const candidates: unknown[] = [];
  const v = variant;
  const p = product || {};

  // Спершу denorm-поля варіанта (downloadURL або голий Storage-шлях)
  if (v) {
    if (v.image) candidates.push(v.image);
    if (v.mainImagePath) candidates.push(v.mainImagePath);
    if (v.mainImageUrl) candidates.push(v.mainImageUrl);
    if (Array.isArray(v.images)) candidates.push(...v.images);
  }

  if (p.coverImageUrl) candidates.push(p.coverImageUrl);
  if (p.mainImagePath) candidates.push(p.mainImagePath);
  if (p.mainImageUrl) candidates.push(p.mainImageUrl);
  if (p.image) candidates.push(p.image);
  if (Array.isArray(p.images)) candidates.push(...p.images);

  const found = candidates
    .map((u) => String(u || "").trim())
    .find((u) => u && (u.startsWith("products/") || /^https?:\/\//.test(u)));

  return found ? mapFirebaseUrlToProxy(found) : "";
}

export function useCardImage(
  product: () => AnyRecord | null | undefined,
  variant: () => AnyRecord | null | undefined,
) {
  const { toSrc, toSrcset, onError: onImgError } = useImageOptimizer();

  const coverUrl = computed(() => pickCover(product(), variant()));

  const largestWidth = CARD_WIDTHS[CARD_WIDTHS.length - 1];

  const imgSrc = computed(() => toSrc(coverUrl.value, largestWidth));

  // undefined, а не "" — щоб Vue прибрав атрибут: інакше після фолбеку
  // браузер добирав би кандидата зі srcset і проігнорував новий src.
  const imgSrcset = computed(() => toSrcset(coverUrl.value, CARD_WIDTHS));

  return { coverUrl, imgSrc, imgSrcset, onImgError };
}
